'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

const tiers = [
  {
    name: 'Starter',
    price: 49,
    properties: '1-5',
    features: ['Work orders', 'Asset management', 'Tenant portal'],
    highlighted: false,
  },
  {
    name: 'Growth',
    price: 129,
    properties: '6-25',
    features: ['Everything in Starter', 'Vendor portal', 'Reporting'],
    highlighted: false,
  },
  {
    name: 'Professional',
    price: 249,
    properties: '26-100',
    features: ['Everything in Growth', 'Advanced analytics', 'API access'],
    highlighted: true,
    badge: 'POPULAR',
  },
  {
    name: 'Business',
    price: 499,
    properties: '100+',
    features: ['Everything in Pro', 'Tax Center included', 'White-label'],
    highlighted: false,
  },
];

const PricingPreview = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="pricing"
      className="relative py-14 sm:py-20 lg:py-24 overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]">
          <div className="absolute inset-0 bg-gradient-radial from-primary-500/[0.04] to-transparent rounded-full blur-3xl" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span
            className={cn(
              "inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase",
              "bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            Pricing
          </span>
          <h2
            className={cn(
              "font-playfair text-3xl md:text-4xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            Plans That Scale
            <span className="text-primary-600 dark:text-primary-400"> With You</span>
          </h2>
          <p
            className={cn(
              "max-w-xl mx-auto text-gray-500 dark:text-secondary-400",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '200ms' }}
          >
            Launching Q3 2026. Join the waitlist for early access.
          </p>
        </div>

        {/* Compact Tier Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {tiers.map((tier, i) => (
            <div
              key={tier.name}
              className={cn(
                "relative rounded-2xl p-4 sm:p-5 flex flex-col",
                tier.highlighted
                  ? "bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-[#141414] border-2 border-primary-500/40"
                  : "bg-white shadow-sm dark:bg-white/[0.03] dark:shadow-none border border-gray-200 dark:border-white/[0.06]",
                "transition-all duration-500",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: `${300 + i * 75}ms` }}
            >
              {tier.badge && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                  <span className="px-2.5 py-0.5 bg-primary-500 text-white text-[10px] font-bold rounded-full">
                    {tier.badge}
                  </span>
                </div>
              )}

              <h3 className="text-sm font-bold text-gray-900 dark:text-secondary-100 mb-1">{tier.name}</h3>
              <p className="text-[10px] text-gray-400 dark:text-secondary-500 mb-3">{tier.properties} properties</p>

              <div className="flex items-baseline gap-0.5 mb-3">
                <span className="text-2xl font-bold text-gray-900 dark:text-secondary-100 font-playfair">${tier.price}</span>
                <span className="text-gray-400 dark:text-secondary-500 text-xs">/mo</span>
              </div>

              <div className="space-y-1.5 flex-grow">
                {tier.features.map((feature, j) => (
                  <div key={j} className="flex items-center gap-1.5">
                    <Check className={cn(
                      "w-3 h-3 flex-shrink-0",
                      tier.highlighted ? "text-primary-600 dark:text-primary-400" : "text-gray-400 dark:text-secondary-500"
                    )} />
                    <span className="text-xs text-gray-600 dark:text-secondary-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Link to full pricing */}
        <div
          className={cn(
            "text-center",
            "transition-all duration-700",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          )}
          style={{ transitionDelay: '700ms' }}
        >
          <Link
            href="/pricing"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
          >
            See full pricing, add-ons & comparison
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export { PricingPreview };
